/**
 * COMPOSED 위임 외부 작성 공개 페이지 (Phase 3b).
 *
 * URL: /external/composed/:token (무인증 — 토큰 기반)
 * 서버 계약: apps/server/src/reports/composed-external.controller.ts
 *
 * DLR 외부 페이지(daily-inspection-log/[token].tsx)와 구조 동형:
 *  - 상태 머신 loading/ready/expired/completed/error
 *  - 만료 D-day 배너 + 에러 코드 분기(REPORT_NOT_FOUND / VALIDATION_INVALID_INPUT → expired)
 *  - onChange → debounce PATCH, 완료 → 필수 검증 → WorkplaceSignModal → complete API
 *  - REJECTED 재작성(반려 사유 배너 + 편집 허용)
 *  - 콤보 후보 rows는 서버 동봉(catalogRows), 담당자 등록은 공개 site-contacts 엔드포인트
 *
 * 묶음 위임(§6, 결정 2026-09-06): 토큰 1개가 확장 산출 N건 전체의 작성 수단이다.
 *  - **확장 전 placeholder면 작성 폼 대신 그룹 선택 게이트가 먼저 뜬다**(SplitGroupGateScreen doc —
 *    그룹 1의 in-place claim이 확장 전 임시 저장분을 덮으므로 순서가 뒤집히면 안 된다).
 *  - 확장 후에는 `?reportId=`로 형제 사이를 오간다(같은 토큰 재조회). 서버가 형제 술어로 검증하므로
 *    페이지는 소속을 판정하지 않는다.
 *  - 외부에는 배포 설정 진입이 없다(§3 주체 축 — 설정은 위임자 in-app 몫).
 *
 * 작성 UI는 인증 작성 페이지(reports/new-composed.tsx)와 같은 rc 조립 렌더러
 * (`ComposerWidgetTreeRenderer`)를 재사용한다. 렌더·검증이 읽는 정본은 `writeTree` 하나 —
 * 저작 트리(`composerWidgetTree`)가 있으면 그것, 없으면 본문 바인딩 파생(`deriveWriteForm`).
 * (옛 flat 렌더 갈래는 2026-09-30 S3-b 철거, 옛 flat aiSelect 박제 소비 경로는 2026-07-25 철거 —
 *  공개 경로에서 AI 재생성은 여전히 금지.)
 *
 * 주의: 인증 store(useAuthStore)/인증 API import 금지. 사진(photo) 위젯은 공개 업로드 경로
 * 부재로 안내 placeholder로 degrade(`onPhotoUpload` 미배선 — 업로드 시도 없음).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@safetyhub/ui';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  applyVariableDynamicOptions,
  collectMissingRequired,
  collectSortableSelectOptionSources,
  collectSubmitActionCatalogKeys,
  collectWidgetDefaultValues,
  deriveWriteForm,
  focusMissingRequiredField,
  missingRequiredToastMessage,
  useActiveComposerView,
  type MissingRequiredField,
} from '@safetyhub/report-composer';
import {
  AI_SUGGEST_BAKED_CONTENT_KEY,
  CATALOG_PARAM_RESOLUTION_KEY,
  COMPOSER_IMPROVEMENT_CANDIDATE_PART_KEYS,
  DEFAULT_COMPOSE_CHROME,
  type ComposerWidget,
  type InputWidget,
  type ReportTemplate,
  type SplitCreateGroupSelection,
  type SplitPlanResponse,
} from '@safetyhub/shared';
import {
  collectImprovementParts,
  improvementCandidatesToRows,
} from '../../../components/reports/composer/improvementCandidates';
import {
  getComposedDraft,
  getComposedSiblingsByToken,
  getComposedSplitPlanByToken,
  expandComposedByToken,
  updateComposedDraft,
  completeComposedDraft,
  createComposedSiteContactByToken,
  type ComposedExternalDraftResponse,
} from '../../../lib/composed-external';
import { apiErrorMessage } from '../../../lib/publicTokenApi';
import { usePublicTokenSocket } from '../../../hooks/usePublicTokenSocket';
import { resolveSiblingIdAfterRemoval } from '../../reports/[id]/nextSiblingHref';
import { SplitGroupSelectModal } from '../../../components/reports/SplitGroupSelectModal';
import SplitGroupGateScreen from './SplitGroupGateScreen';
import { SplitSiblingBar, nextWritableSibling } from './SplitSiblingBar';
import { useToastStore } from '../../../stores/toast';
import { ComposerWidgetTreeRenderer } from '../../../components/reports/composer/WriteFormRenderer';
import { COMPOSED_ROW_WIDGETS } from '@safetyhub/report-composer';
import { COMPOSED_FORM_WIDGETS } from '../../../components/reports/composer/registeredFormWidgets';
import { WorkplaceSignModal } from '../../../components/approval/WorkplaceSignModal';
import { ReportWriteShell } from '../../../components/reports/ReportWriteShell';
import { ComposePreviewView } from '../../../components/reports/composer/ComposePreviewView';

type PageState = 'loading' | 'ready' | 'expired' | 'completed' | 'error';

/**
 * 공개(무인증 토큰) 경로가 등록을 지원하는 카탈로그 — 서버에 site-contacts 엔드포인트 하나뿐이다
 * (인증 경로의 범용 `POST /report-composer/catalog/:key/rows`는 무인증으로 열려 있지 않다).
 */
const PUBLIC_REGISTRABLE_CATALOG_KEYS = new Set(['site-contacts']);

/**
 * 카탈로그 rows 미해소 장애(catalogRowsUnavailable) 시 선택 전용(requireSelection) 콤보의
 * **자유 입력 강등** — 렌더 트리에서 requireSelection 선언만 제거한다(rc가 이미 가진 축 활용:
 * 선언이 없으면 종전 자유 입력 콤보 그대로). 은닉 변조가 아니라 "장애 시 완화"다 — 서버가 고를
 * 목록을 못 만든 상황에서 선언을 그대로 두면 읽기 전용 degrade(rc 규약)로 작성 자체가 교착되므로,
 * 상단 안내 배너와 함께 이번 작성에 한해 직접 입력을 허용한다. 정상 응답(rows 동봉)이면 미호출.
 */
function demoteRequireSelection(tree: ComposerWidget[]): ComposerWidget[] {
  const demoteInput = <T extends { requireSelection?: boolean }>(w: T): T => {
    if (w.requireSelection !== true) return w;
    const next = { ...w };
    delete next.requireSelection;
    return next;
  };
  return tree.map((w) => {
    if (w.kind === 'input') return demoteInput(w);
    if (w.kind === 'group' || w.kind === 'repeat' || w.kind === 'modalTrigger') {
      return { ...w, children: demoteRequireSelection(w.children) };
    }
    if (w.kind === 'branch') {
      return {
        ...w,
        branches: w.branches.map((slot) => ({
          ...slot,
          children: demoteRequireSelection(slot.children),
        })),
      };
    }
    if (w.kind === 'formTable') {
      return {
        ...w,
        rows: w.rows.map((row) =>
          row.type === 'fixed'
            ? {
                ...row,
                cells: row.cells.map((cell) =>
                  cell.widget ? { ...cell, widget: demoteInput(cell.widget) } : cell,
                ),
              }
            : row,
        ),
      };
    }
    return w;
  });
}

/**
 * 토큰 축 무효(만료·비활성·부재) 판정 — GET 경로의 expired 분기와 같은 코드 축.
 * POST(등록)에서는 같은 VALIDATION_INVALID_INPUT 코드가 **입력 오류에도** 쓰이므로
 * (cap 초과=details.message, DTO 검증=details.errors), 입력 형상 details가 없는 경우에만
 * 토큰 무효로 본다 — validateToken의 만료/비활성 throw는 details 없이 코드만 싣는다.
 */
function isTokenInvalidError(err: unknown): boolean {
  const apiErr = (
    err as { error?: { code?: string; details?: { message?: string; errors?: unknown } } }
  )?.error;
  if (!apiErr) return false;
  if (apiErr.code === 'REPORT_NOT_FOUND') return true;
  return (
    apiErr.code === 'VALIDATION_INVALID_INPUT' &&
    !apiErr.details?.message &&
    !apiErr.details?.errors
  );
}

function daysUntil(isoDate: string): number {
  const diff = new Date(isoDate).getTime() - Date.now();
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
}

/** 작성폼 노드 + 템플릿의 표(dataDef) 기본행으로 초기값 seed (new-composed 동형). */
function deriveInitialValues(
  tpl: ReportTemplate,
  formNodes: InputWidget[],
): Record<string, unknown> {
  const initial: Record<string, unknown> = {};
  for (const node of formNodes) {
    if (node.defaultValue !== undefined) {
      initial[node.bindingKey] = node.defaultValue;
    }
    if (node.widget === 'table') {
      const def = tpl.dataDefs?.find((d) => d.key === node.bindingKey);
      if (def) {
        initial[node.bindingKey] = structuredClone(def.defaultRows);
      }
    }
  }
  // 트리(composerWidgetTree) 저작 defaultValue를 flat 파생분 위에 덮는다(new-composed 동형 —
  // 같은 bindingKey면 트리 위젯 저작값이 정본). 서버 content는 호출부에서 이 위에 다시 덮는다.
  Object.assign(initial, collectWidgetDefaultValues(tpl.composerWidgetTree ?? [], tpl.dataDefs));
  return initial;
}

export default function ExternalComposed() {
  const { token } = useParams<{ token: string }>();
  /**
   * 묶음 안에서 지금 작성 중인 문서 — URL 검색 파라미터에 둔다(새로고침·뒤로가기 보존).
   * 미지정이면 토큰 문서(앵커)다. 요청에도 이 값을 그대로 싣는다 — 비묶음 경로에서는 항상
   * undefined라 종전 요청 형상이 1비트도 바뀌지 않는다(무회귀).
   */
  const [searchParams, setSearchParams] = useSearchParams();
  const activeReportId = searchParams.get('reportId') ?? undefined;
  // 공개 경로에도 ToastContainer는 항상 마운트돼 있다(App 최상단 — 인증 분기 밖).
  const showToast = useToastStore((s) => s.showToast);
  // 활성 반응형 뷰 — 필수 검증에서 현재 뷰 숨김 위젯을 제외하는 축(인증 작성 페이지 동형).
  const activeView = useActiveComposerView();
  const [pageState, setPageState] = useState<PageState>('loading');
  const [draft, setDraft] = useState<ComposedExternalDraftResponse | null>(null);
  const [nodes, setNodes] = useState<InputWidget[]>([]);
  const [values, setValues] = useState<Record<string, unknown>>({});
  // 카탈로그 후보 rows(소스 키별) — GET 응답 catalogRows로 seed하고, 공개 담당자 등록 성공 시
  // 서버 재조회분으로 갱신한다(rows 정본은 서버 — 파생 표시 필드까지 서버 산식이 소유).
  const [catalogRows, setCatalogRows] = useState<Record<string, Record<string, unknown>[]>>({});
  const [showSignModal, setShowSignModal] = useState(false);
  // 작성/미리보기 뷰 전환(new-composed와 동일 — values는 useState라 전환에도 보존).
  const [mode, setMode] = useState<'edit' | 'preview'>('edit');
  /** 미리보기 진입 직전 작성 화면 스크롤 위치 — 돌아올 때 복원용(ComposePreviewView에 전달). */
  const previewFromScrollYRef = useRef(0);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(false);
  const [isCompleting, setIsCompleting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  // 묶음 위임 — 그룹 선택 게이트 상태.
  const [splitPlan, setSplitPlan] = useState<SplitPlanResponse | null>(null);
  const [isPlanLoading, setIsPlanLoading] = useState(false);
  const [planError, setPlanError] = useState<string | undefined>(undefined);
  const [isExpanding, setIsExpanding] = useState(false);
  /**
   * 게이트를 지나갔다는 표시 — 계획이 `plan: null`(분할 대상 없음)이거나 담당자가 통합을 고른
   * 경우다. 둘 다 확장을 하지 않으므로 서버 응답만으로는 "아직 안 골랐다"와 구분되지 않는다.
   */
  const [groupSelectionSkipped, setGroupSelectionSkipped] = useState(false);
  /** 완료 화면에서 이어 작성할 다음 형제 — 없으면 묶음 전체(또는 단건) 완료다. */
  const [nextSibling, setNextSibling] = useState<{ reportId: string; label: string } | null>(
    null,
  );
  /** 재조회 트리거 — 확장 직후처럼 `activeReportId`가 그대로인 채 다시 읽어야 할 때 쓴다. */
  const [reloadNonce, setReloadNonce] = useState(0);

  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isCompletedRef = useRef(false);
  // 항상 최신 병합 values를 참조(디바운스 저장이 stale 클로저를 읽지 않도록).
  const valuesRef = useRef<Record<string, unknown>>({});
  valuesRef.current = values;
  // 디바운스 저장이 **그 시점의 대상 문서**로 나가도록(문서를 바꾼 뒤 옛 값이 새 문서에 실리는
  // 사고 방지). 전환 시 타이머를 먼저 비우지만, 클로저가 stale이면 그 가드가 무의미해진다.
  const activeReportIdRef = useRef<string | undefined>(activeReportId);
  activeReportIdRef.current = activeReportId;
  /**
   * 아직 서버로 안 나간 편집이 있는가 — 형제 전환 시 **불필요한 PATCH를 막는 축**이다.
   * 빈 이동마다 저장을 쏘면 `lastDraftSavedAt`·`DRAFT_SAVED` 감사가 늘어, 위임자 화면이
   * 실제로는 쓰지 않은 문서를 "작성 중"으로 읽는다(서버 updateDraftByToken 주석과 같은 원칙:
   * 신호는 실제 쓰기에만 붙인다).
   */
  const hasUnsavedRef = useRef(false);

  // unmount 시 dangling timeout 차단(완료 가드는 남기지 않음 — StrictMode 재마운트 영구차단 방지).
  useEffect(() => {
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, []);

  // AI 개선 의견 후보 rows — draft 응답의 **캐시 히트 한정** 동봉분(improvementCandidates)을
  // 파트별 관례 키 rows로 변환해 resolvedRows로 주입(인증 작성 페이지 new-composed의 런타임
  // resolve 대체 — external은 인증 API 호출 불가라 서버가 위임 생성 시 사전 생성한 캐시만 소비).
  // 미동봉(캐시 미스)이면 undefined = 후보 없음 = 모달 버튼 hideWhenNoData 자동 숨김(기존 동형).
  const aiResolvedRows = useMemo(() => {
    const tree = draft?.template?.composerWidgetTree;
    const candidates = draft?.improvementCandidates;
    if (!tree?.length || !candidates) return undefined;
    const rows: Record<string, Record<string, unknown>[]> = {};
    for (const part of collectImprovementParts(tree)) {
      const partRows = improvementCandidatesToRows(candidates, [part]);
      if (partRows.length > 0) rows[COMPOSER_IMPROVEMENT_CANDIDATE_PART_KEYS[part]] = partRows;
    }
    return Object.keys(rows).length > 0 ? rows : undefined;
  }, [draft]);

  // rc에 넘길 후보 rows 단일 채널 — 카탈로그(서버 동봉/등록 후 재조회) + AI 후보 합류.
  // AI 파트 키(improvement-candidates:*)와 카탈로그 키는 겹치지 않지만, 겹칠 경우 AI가 정본이
  // 되도록 뒤에 둔다(인증 작성 페이지 new-composed의 `{...listRows, ...prev}` 병합 순서 동형).
  // 둘 다 비면 undefined(프로퍼티 미주입) — 기존 degrade 동작 보존.
  const resolvedRows = useMemo(() => {
    const merged = { ...catalogRows, ...(aiResolvedRows ?? {}) };
    return Object.keys(merged).length > 0 ? merged : undefined;
  }, [catalogRows, aiResolvedRows]);

  // 저작 트리 — 카탈로그 rows 미해소 장애면 requireSelection 강등본(demote 주석 참조).
  const effectiveTree = useMemo(() => {
    const tree = draft?.template?.composerWidgetTree;
    if (!tree?.length) return tree;
    return draft?.catalogRowsUnavailable ? demoteRequireSelection(tree) : tree;
  }, [draft]);
  /**
   * 작성 폼의 **단일 정본 트리** — 렌더(ComposerWidgetTreeRenderer)와 필수 검증
   * (collectMissingRequired)이 같은 값을 읽는다(2026-09-30 S3-b).
   *
   * 저작 트리가 있으면 그것(변수 동적 옵션 반영 — 서버가 동봉하지 않은 소스는 정적 options 폴백으로
   * degrade, 무크래시 규약. 배치본 stale options만 정정), 없으면 본문 바인딩 파생 `nodes`
   * (= `deriveWriteForm` 산출 `InputWidget[]` — `ComposerWidget` union 멤버라 변환층이 없다).
   *
   * ⚠️ 파생 쪽에 `applyVariableDynamicOptions`도 `demoteRequireSelection`도 걸지 않는다 — 둘 다
   * **저작 트리 전용 처방**이고 파생 입력에는 대상이 없다: 파생은 매번 `template.variables`를 라이브로
   * 읽어 같은 `optionsFrom`·`options`를 이미 싣고(rc `writeForm.ts:450`), `suggestionsFrom`
   * (=`requireSelection`이 사는 곳)은 **저작만 채우는 축**이라 `writeForm.ts`에 0건이다.
   */
  const writeTree = useMemo<ComposerWidget[]>(
    () =>
      // `effectiveTree &&` 형태로 쓴다 — `effectiveTree?.length ?` 삼항도 좁혀지지만, 이 값이
      // `ComposerWidget[] | undefined`라 옵셔널 체인 narrowing에 의존하지 않는 쪽이 읽기에 분명하다.
      effectiveTree && effectiveTree.length > 0
        ? applyVariableDynamicOptions(effectiveTree, draft?.template?.variables)
        : nodes,
    [effectiveTree, draft?.template?.variables, nodes],
  );

  /** 이 템플릿의 등록 모달 중 공개 경로가 실제로 처리할 수 있는 카탈로그가 하나라도 있는가. */
  const hasPublicRegistrableAction = useMemo(
    () =>
      collectSubmitActionCatalogKeys(draft?.template?.composerWidgetTree ?? []).some((k) =>
        PUBLIC_REGISTRABLE_CATALOG_KEYS.has(k),
      ),
    [draft],
  );

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    // 새 대상 문서를 읽는 참이다 — 앞 문서의 완료 가드를 넘겨받으면 그 문서는 저장이 영영
    // 막힌다(가드는 "이 문서 작성을 끝냈다"는 뜻이지 토큰 전체가 아니다).
    isCompletedRef.current = false;
    hasUnsavedRef.current = false;

    (async () => {
      try {
        const data = await getComposedDraft(token, activeReportId);
        if (cancelled) return;
        setDraft(data);

        const tpl = data.template;
        if (!tpl || !data.content) {
          setPageState('error');
          setErrorMessage('작성 양식을 불러올 수 없습니다.');
          return;
        }
        // 인증 작성 페이지(new-composed)와 같은 규약 — 작성 폼 골격의 출처는 **본문 바인딩 파생**
        // 하나다(저작 트리가 있으면 아래 `writeTree` memo가 그것을 우선). 종전의 「저장 배열이 있으면
        // 그것」 삼항은 값이 있으면 파생 골격을 버렸고(2026-09-30 S3-a), 그 저장 배열 자체는
        // 2026-10-02(S6)에 철거됐다.
        // ⚠️ 카탈로그를 넘기지 않으므로 스캐폴드 라벨은 `label = bindingKey`다(카탈로그 input entry가
        // 주는 라벨·`validation.required`는 이 경로에서 실리지 않는다).
        const formNodes = deriveWriteForm(tpl);
        setNodes(formNodes);
        // 카탈로그 rows seed = 박제 content의 소스 키 rows(위임 생성 시 서버 prefill 채널이 박제 —
        // 기준표 criteria rows 등. 행 스코프 렌더에선 values 채널이 닿지 않아 resolvedRows로 줘야
        // select optionsFrom이 산다. 인증 작성 페이지 extractListRowsFromPrefill 동형 규약) 위에
        // 서버 동봉 catalogRows(라이브 정본 — site-contacts 등)를 덮어씀.
        const bakedRows: Record<string, Record<string, unknown>[]> = {};
        for (const key of collectSortableSelectOptionSources(tpl.composerWidgetTree ?? [])) {
          const rows = (data.content as Record<string, unknown>)[key];
          if (Array.isArray(rows)) bakedRows[key] = rows as Record<string, unknown>[];
        }
        setCatalogRows({ ...bakedRows, ...(data.catalogRows ?? {}) });
        // 초기값 = 파생 기본값 위에 서버 content(담당자 저장분 + 박제분)를 덮어씀.
        setValues({ ...deriveInitialValues(tpl, formNodes), ...data.content });
        setPageState('ready');
      } catch (err: unknown) {
        if (cancelled) return;
        const apiErr = (err as { error?: { code?: string; message?: string } })?.error;
        /**
         * **형제가 사라진 것과 토큰이 만료된 것은 다르다**(2026-09-10 사용자 적발).
         *
         * 위임자가 묶음 중 한 건을 지우면(보고서 삭제는 하드 삭제다) 담당자 화면의 형제 탭은
         * 그 순간 낡을 수 있다. 실시간 경로는 토큰 방(`usePublicTokenSocket` — 아래 배선)이
         * 맡지만, 소켓이 끊긴 사이·이벤트가 닿기 전에 그 탭을 누르면 서버가 `REPORT_NOT_FOUND`를
         * 낸다. 종전엔 그것을 만료와 같은 갈래로 접어 **페이지 전체가 「만료」 화면**이 됐다.
         * 토큰은 멀쩡한데 돌아갈 길이 없는 막다른 길이다.
         *
         * 형제를 보던 중(`activeReportId` 있음)이라면 그 파라미터만 떨구고 **토큰 문서로
         * 되돌아간다** — 그 재조회가 형제 목록도 새로 받아 오므로 낡은 탭도 함께 사라진다.
         * 토큰 문서 자체가 없는 경우(파라미터 없음)는 종전대로 만료가 맞다. 이 분기는 토큰 방
         * 배선 뒤에도 **폴백으로 남는다**.
         */
        if (apiErr?.code === 'REPORT_NOT_FOUND' && activeReportId) {
          showToast('그 문서는 삭제되었습니다. 남은 문서로 돌아갑니다.', 'warning');
          setSearchParams({});
          return;
        }
        if (
          apiErr?.code === 'VALIDATION_INVALID_INPUT' ||
          apiErr?.code === 'REPORT_NOT_FOUND'
        ) {
          setPageState('expired');
        } else {
          setPageState('error');
          setErrorMessage(apiErr?.message ?? '서버에 연결할 수 없습니다.');
        }
      }
    })();

    return () => {
      cancelled = true;
    };
    // activeReportId: 형제 전환 시 그 문서를 다시 읽는다. reloadNonce: 확장 직후처럼 대상이
    // 그대로인 채 다시 읽어야 하는 경우(파라미터만 보면 effect가 재실행되지 않는다).
    // showToast·setSearchParams는 안정 참조(zustand·react-router) — 의존성에 넣으면 effect가
    // 매 렌더 재실행돼 draft를 반복 조회한다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, activeReportId, reloadNonce]);

  /**
   * 형제 목록만 다시 받는다(경량 엔드포인트) — 토큰 방 이벤트·재연결 뒤 쓴다.
   * `getComposedDraft`로 재조회하지 않는다: 본문이 통째로 와 **입력 중인 값을 덮는다**(결정 §3).
   * 응답이 null(묶음 아님)이면 건드리지 않는다 — 없는 목록으로 있던 목록을 지우지 않기 위해서다.
   * (게이트 판정은 2026-09-10부터 이 목록이 아니라 `draft.splitGroupKey` 자기 필드가 쥔다 —
   *  그 대리 판정이 무너져 그룹 선택이 통째로 건너뛰어진 사고의 처방이다. `needsGroupSelection` doc.)
   * 토큰이 죽어 있으면(전부 지워짐 P4·비활성) REST와 같은 코드로 실패한다 → 만료 화면.
   */
  const refreshSiblings = useCallback(async () => {
    if (!token) return;
    try {
      const res = await getComposedSiblingsByToken(token);
      if (!res.splitSiblings) return;
      setDraft((prev) => (prev ? { ...prev, splitSiblings: res.splitSiblings ?? undefined } : prev));
    } catch (err) {
      if (isTokenInvalidError(err)) {
        setPageState('expired');
      }
      // 그 밖의 실패(네트워크)는 조용히 — 다음 이벤트·재연결이 다시 시도한다.
    }
  }, [token]);

  /**
   * 토큰 방(`/ws/public`) 배선 — 화면 계약은 결정 2026-09-10-public-token-websocket-room §5.
   *
   * - `deleted`: 목록을 다시 받는다(P1 — 보고 있지 않은 형제면 탭만 조용히 사라진다). **보고 있는
   *   문서**면 안내 + 남은 문서로 옮긴다(P2) — 목적지는 인증 상세와 **같은 규칙**
   *   (`resolveSiblingIdAfterRemoval`: 다음 탭, 없으면 앞 탭). 판정은 **지워지기 전 목록**
   *   (`draft.splitSiblings`)으로 한다 — 새 목록엔 지워진 문서가 없어 「다음」을 셀 수 없다.
   *   앵커가 지워져도 토큰은 재앵커돼 살아 있으므로(P3) 같은 처리다. 미저장 입력은 버려진다 —
   *   문서가 없어졌으니 살릴 곳이 없다.
   * - `expanded`: 다른 창의 같은 담당자가 확장했다(P6). 그룹 1이 placeholder를 in-place로 채워
   *   본문까지 바뀌었으므로 형제 목록만으로는 부족하다 — 확장한 창과 같은 경로로 앵커를 다시
   *   읽는다(`handleConfirmSplit`과 동형). 확장한 창 자신도 이 이벤트를 받지만 그 재조회는
   *   이미 진행 중이라 `cancelled` 가드가 앞선 것을 버린다.
   * - `revoked`: 위임 취소·재발송·해제·인앱 제출(P5) — 작성 중인 화면만 만료로 접는다. 완료
   *   화면(마지막 건 완료 뒤 위임자가 제출)에서는 그대로 둔다 — 담당자 일은 끝났다.
   * - 재연결(P7): 끊긴 사이의 사건을 놓치지 않게 목록을 한 번 다시 받는다.
   *
   * 폴백: 이벤트가 닿기 전에 낡은 탭을 누르면 위 `REPORT_NOT_FOUND` 분기가 토큰 문서로 되돌린다.
   */
  const pageStateRef = useRef<PageState>(pageState);
  pageStateRef.current = pageState;
  usePublicTokenSocket({
    kind: 'composed-draft',
    token,
    enabled: pageState !== 'expired',
    onBundleChanged: (payload) => {
      if (payload.change === 'expanded') {
        if (pageStateRef.current !== 'ready' && pageStateRef.current !== 'loading') return;
        setSplitPlan(null);
        setPageState('loading');
        setSearchParams({});
        setReloadNonce((n) => n + 1);
        return;
      }
      const viewingId = draft?.reportId;
      const previous = draft?.splitSiblings ?? [];
      if (pageStateRef.current === 'ready' && viewingId && payload.reportId === viewingId) {
        const target = resolveSiblingIdAfterRemoval(
          previous.map((s) => ({ id: s.reportId })),
          viewingId,
        );
        showToast('그 문서는 삭제되었습니다. 남은 문서로 돌아갑니다.', 'warning');
        if (debounceTimer.current) clearTimeout(debounceTimer.current);
        hasUnsavedRef.current = false;
        if (target) {
          setSearchParams({ reportId: target });
        } else {
          // 남은 문서를 모른다(단건 위임·형상 불일치) — 앵커로 되돌아가 서버 판정을 받는다.
          setSearchParams({});
          setReloadNonce((n) => n + 1);
        }
      }
      void refreshSiblings();
    },
    onTokenRevoked: () => {
      if (pageStateRef.current !== 'ready' && pageStateRef.current !== 'loading') return;
      showToast('위임이 취소되거나 다시 발송되어 이 링크는 더 이상 쓸 수 없습니다.', 'warning');
      setPageState('expired');
    },
    onReconnect: () => {
      void refreshSiblings();
    },
  });

  // PATCH 페이로드에서 서버 관리 박제 키는 제외 — 담당자 편집분만 전송.
  // ⚠️ AI 후보(AI_SUGGEST_BAKED_CONTENT_KEY)의 생산 경로는 2026-07-25 철거됐다(flat aiSelect).
  //    철거 이전 생성분의 content에는 아직 남아 있을 수 있어 하위호환으로 계속 걸러낸다.
  // 파라미터 해소 근거(CATALOG_PARAM_RESOLUTION_KEY)는 compose가 쓰는 사후 검증용 박제라
  //    외부 작성자가 되돌려 보낼 값이 아니다(왕복시키면 근거가 편집 가능해진다).
  const buildSavePayload = useCallback((v: Record<string, unknown>): Record<string, unknown> => {
    const rest = { ...v };
    delete rest[AI_SUGGEST_BAKED_CONTENT_KEY];
    delete rest[CATALOG_PARAM_RESOLUTION_KEY];
    return rest;
  }, []);

  /**
   * 카탈로그 행 즉시 등록(submitAction 모달) 호스트 콜백 — 공개 경로판.
   *
   * 등록 응답이 **카탈로그 rows 행 형상 투영**(레거시 파생 표시 필드 display 포함 — 산식은
   * 서버 소유, 계약 2026-08-24)이므로 재조회 없이 그 행이 정본이다. 해당 catalogKey rows에만
   * **키 단위 병합**(id 일치 교체, 아니면 append — new-composed 동형)하고 그 행을 rc에 돌려준다.
   * ⚠️ 전체 교체(setCatalogRows(fresh.catalogRows)) 금지 — 재조회 응답이 rows를 생략/부분 반환하면
   * 다른 카탈로그 rows까지 전멸한다(리뷰 W4). 옛 "재조회 실패 낙관 append" 분기는 재조회 제거로 소멸.
   *
   * 실패는 null 반환(rc가 모달 유지 + 인라인 오류) + 서버 문구 토스트(cap 10 초과·연락처 형식 등).
   */
  const handleCatalogRowSubmit = useCallback(
    async (
      catalogKey: string,
      submitValues: Record<string, string>,
    ): Promise<Record<string, unknown> | null> => {
      if (!token) return null;
      if (!PUBLIC_REGISTRABLE_CATALOG_KEYS.has(catalogKey)) {
        // 공개 경로 미지원 카탈로그 — 막다른 길로 두지 않고 대안(담당자 문의)을 안내한다.
        showToast(
          '이 항목은 위임 작성 링크에서 등록할 수 없습니다. 담당 안전관리자에게 문의해주세요.',
          'error',
        );
        return null;
      }
      // 서버 DTO whitelist(forbidNonWhitelisted)라 선언된 필드만 싣는다 — fieldMap이 그 외 필드를
      // 매핑했더라도 그대로 보내면 400. 빈 선택 필드는 생략(서버 @IsOptional).
      const pick = (key: string): string | undefined => {
        const v = submitValues[key];
        return typeof v === 'string' && v.trim() !== '' ? v.trim() : undefined;
      };
      try {
        const created = await createComposedSiteContactByToken(token, {
          name: submitValues.name?.trim() ?? '',
          phone: submitValues.phone?.trim() ?? '',
          department: pick('department'),
          position: pick('position'),
          email: pick('email'),
        });
        const row = created as unknown as Record<string, unknown>;
        setCatalogRows((prev) => {
          const cur = prev[catalogKey] ?? [];
          // 같은 행(id 일치) 갱신이면 교체, 아니면 append — 그 catalogKey rows만 만진다.
          const idx = cur.findIndex((r) => r.id === created.id);
          return {
            ...prev,
            [catalogKey]: idx >= 0 ? cur.map((r, i) => (i === idx ? row : r)) : [...cur, row],
          };
        });
        return row;
      } catch (err) {
        // 토큰 만료/무효는 토스트로 끝내지 않는다 — 이 링크로는 더 진행할 수 없으므로 GET 경로와
        // 같은 만료 화면으로 전이(W9). 입력 오류(cap·형식)는 종전대로 토스트 + 모달 유지.
        if (isTokenInvalidError(err)) {
          setPageState('expired');
          return null;
        }
        showToast(apiErrorMessage(err, '확인자 추가에 실패했습니다.'), 'error');
        return null;
      }
    },
    [token, showToast],
  );

  const handleChange = useCallback(
    (bindingKey: string, value: unknown) => {
      setValues((prev) => ({ ...prev, [bindingKey]: value }));
      setSaveError(false);
      if (!token || isCompletedRef.current) return;
      hasUnsavedRef.current = true;
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
      debounceTimer.current = setTimeout(async () => {
        if (isCompletedRef.current) return;
        setIsSaving(true);
        try {
          await updateComposedDraft(
            token,
            buildSavePayload(valuesRef.current),
            activeReportIdRef.current,
          );
          hasUnsavedRef.current = false;
          /**
           * 형제 바의 「작성 전 → 작성 중」을 **그 자리에서** 반영한다(2026-09-10 사용자 적발:
           * 새로고침해야 바뀌었다). 서버는 위임자 화면에 WS로 알리지만 이 화면은 무인증이라
           * 그 방을 듣지 않는다 — 재조회 대신 방금 저장한 문서 한 칸만 로컬로 올린다.
           * (표식은 불리언이라 「이미 true」면 참조가 그대로 유지돼 불필요한 렌더도 없다.)
           */
          setDraft((prev) => {
            if (!prev?.splitSiblings) return prev;
            // 대상은 저장이 나간 그 문서 — 형제 미지정 저장은 토큰 문서(앵커)에 실린다.
            const savedReportId = activeReportIdRef.current ?? prev.reportId;
            if (prev.splitSiblings.some((s) => s.reportId === savedReportId && s.hasDelegateDraft)) {
              return prev;
            }
            return {
              ...prev,
              splitSiblings: prev.splitSiblings.map((s) =>
                s.reportId === savedReportId ? { ...s, hasDelegateDraft: true } : s,
              ),
            };
          });
        } catch {
          setSaveError(true);
        } finally {
          setIsSaving(false);
        }
      }, 3000);
    },
    [token, buildSavePayload],
  );

  /**
   * 완료 전 필수(required) 미입력 안내 — 인증 작성 페이지(new-composed handleSubmit)와 동형.
   * 라벨의 "N번째 행 · " prefix는 rc가 이미 붙여 준다(반복 행 스코프).
   * 서버도 complete에서 같은 규칙으로 검증하므로(우회 대비 최종 방어선), 이 게이트는 왕복 전
   * 안내이지 유일한 방어선이 아니다.
   *
   * ⚠️ 인자 타입은 **rc 계약에서 파생**한다 — 손으로 줄여 적으면(`{id,label}[]`) 나중에 늘어난
   * 축(`requirement` — 안내 문구를 가르는 값)이 조용히 떨어진다(2026-09-29 tsc 적발).
   * `requirement`만 옵셔널인 이유: 이 화면은 클라 게이트와 **서버 400 응답**(details.missingRequired)
   * 양쪽에서 부르고, 배포 창의 구 서버는 그 축을 안 싣는다(그때는 값 문구로 degrade).
   */
  const warnMissingRequired = useCallback(
    (
      missing: (Pick<MissingRequiredField, 'id' | 'label'> &
        Partial<Pick<MissingRequiredField, 'requirement'>>)[],
    ): void => {
      // 안내 문구·스크롤/포커스는 rc 공용(missingRequiredToastMessage/focusMissingRequiredField)
      // — 토스트 호출만 host 소유. 반복 행 안 위젯은 rc 헬퍼가 접두 매칭으로 첫 행 인스턴스를 잡는다.
      showToast(missingRequiredToastMessage(missing), 'error');
      focusMissingRequiredField(missing[0].id);
    },
    [showToast],
  );

  /** 완료 버튼 — 필수 미입력이면 서명 모달을 열지 않고 안내만 한다. */
  const handleCompleteClick = () => {
    // 렌더와 **같은 트리**(writeTree)를 본다 — 저작 트리든 본문 바인딩 파생이든 검증 규칙은 하나다.
    // ⚠️ 파생 경로에서 이 호출은 **언제나 `[]`**라(카탈로그 없이 파생하므로 required 출처가 없다)
    // 인자를 `effectiveTree ?? []`로 되돌려도 화면이 똑같다 — 이 축의 탐지기는
    // `__tests__/[token].test.tsx`의 **인자 단언**뿐이다.
    const missing = collectMissingRequired(writeTree, values, activeView);
    if (missing.length > 0) {
      warnMissingRequired(missing);
      return;
    }
    setShowSignModal(true);
  };

  const handleManualSave = async () => {
    if (!token || isCompletedRef.current) return;
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    setIsSaving(true);
    setSaveError(false);
    try {
      await updateComposedDraft(token, buildSavePayload(values), activeReportId);
      hasUnsavedRef.current = false;
    } catch {
      setSaveError(true);
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * 형제 전환 — **이 문서의 미저장 편집을 먼저 흘려보낸 뒤** 대상을 바꾼다.
   * 디바운스 타이머를 비우는 것만으로는 부족하다: 비우면 그 편집분이 통째로 사라지고,
   * 안 비우면 3초 뒤 새 문서에 옛 값이 실린다(둘 다 데이터 사고).
   *
   * 흘려보내는 조건이 둘인 이유:
   *  - `hasUnsavedRef` — 안 고친 문서를 이동할 때마다 저장하면 위임자가 보는 진척 신호가 부풀려진다.
   *  - 쓰기 가능 상태 — 결재로 넘어간 형제(비-postSubmit 템플릿)는 PATCH가 400이라, 무조건
   *    저장하면 **읽기만 하려던 이동이 오류로 막힌다**.
   */
  const handleSelectSibling = useCallback(
    async (reportId: string) => {
      if (!token || reportId === draft?.reportId) return;
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
      const isWritable =
        draft?.reportStatus === 'CREATING' ||
        draft?.reportStatus === 'DRAFT' ||
        draft?.reportStatus === 'REJECTED';
      if (hasUnsavedRef.current && isWritable) {
        setIsSaving(true);
        try {
          await updateComposedDraft(
            token,
            buildSavePayload(valuesRef.current),
            activeReportIdRef.current,
          );
          hasUnsavedRef.current = false;
          setSaveError(false);
        } catch {
          // 저장에 실패했으면 이동하지 않는다 — 옮겨 가면 편집분을 되찾을 방법이 없다.
          setSaveError(true);
          showToast('저장하지 못했습니다. 잠시 후 다시 시도해주세요.', 'error');
          return;
        } finally {
          setIsSaving(false);
        }
      }
      setPageState('loading');
      setSearchParams({ reportId });
    },
    [token, draft?.reportId, draft?.reportStatus, buildSavePayload, setSearchParams, showToast],
  );

  /**
   * 아직 그룹을 고르지 않은 묶음 위임 placeholder인가 — **작성 폼보다 게이트가 먼저**인 조건.
   *
   * 판별 축 셋을 AND한다:
   *  - `reportStatus === 'CREATING'` — 위임 탄생 상태(§7). 완료·승격 뒤엔 고를 것이 없다.
   *  - `splitGroupKey` **부재** — 이 문서가 아직 어느 그룹에도 속하지 않았다는 **자기 필드**다.
   *  - 스냅샷 `policy.split` 선언 — 통짜 위임(분할 정책 없는 템플릿)을 게이트로 끌고 가지 않는다.
   *    **live 템플릿이 아니라 스냅샷**을 보는 것은 위임 시점 양식이 정본이기 때문이다.
   *
   * ⚠️ 둘째 축은 종전에 `!draft.splitSiblings`(형제 목록 **부재**)였다 — 「형제가 안 실려 왔다」로
   * 「나는 아직 확장 전이다」를 **대리 판정**한 것이다. 그 대리는 placeholder가 자기만의 배치를
   * 쓸 때만 성립했고, 위임이 살아 있는 묶음에 합류하게 되자(2026-09-10) 미확장 placeholder에도
   * 형제가 실려 와 **그룹 선택 없이 작성 폼으로 직행**했다(사용자 실측: 위임 칩은 제목으로,
   * 받은 쪽은 「1번째 문서」로 떴다 — claim이 안 된 폴백이다).
   *
   * **남의 목록으로 자기 상태를 추론하지 않는다.** 대리 판정은 그 전제가 바뀌는 날 조용히
   * 깨지고, 깨진 자리가 「게이트가 안 뜬다」라 화면만 봐서는 원인이 안 보인다. 서버도 같은 날
   * 같은 축으로 고쳤다(`listSplitSiblings`는 이제 `!anchor.splitGroupKey`면 null) — 두 층이
   * **같은 질문**을 하게 두는 것이 이 수정의 요점이다.
   */
  const needsGroupSelection =
    !!draft &&
    !groupSelectionSkipped &&
    draft.reportStatus === 'CREATING' &&
    !draft.splitGroupKey &&
    !!draft.template?.policy?.split;

  /** 게이트 버튼 — 계획 조회는 **이 조작 1회에만** 붙는다(부수효과 경로, 클라이언트 JSDoc 참조). */
  const handleStartGroupSelection = async () => {
    if (!token || isPlanLoading) return;
    setIsPlanLoading(true);
    setPlanError(undefined);
    try {
      const response = await getComposedSplitPlanByToken(token);
      if (!response.plan) {
        // 분할 대상이 없다 — 그룹 선택 없이 이 문서를 통짜로 작성한다(§6 ②).
        setGroupSelectionSkipped(true);
        return;
      }
      setSplitPlan(response);
    } catch (err) {
      if (isTokenInvalidError(err)) {
        setPageState('expired');
        return;
      }
      setPlanError(apiErrorMessage(err, '문서 구성을 불러오지 못했습니다. 다시 시도해주세요.'));
    } finally {
      setIsPlanLoading(false);
    }
  };

  /** 확장 확정 — 성공하면 그룹 1(placeholder in-place)이 곧 토큰 앵커라 파라미터를 비우고 재조회. */
  const handleConfirmSplit = async (groups: SplitCreateGroupSelection[]) => {
    if (!token || isExpanding) return;
    setIsExpanding(true);
    try {
      const result = await expandComposedByToken(token, groups);
      if (result.skipped.length > 0) {
        // 서버가 건너뛴 그룹(이미 같은 그룹 문서가 있음) — 조용히 삼키면 고른 것과 만들어진
        // 것이 갈린 채로 작성이 시작된다.
        showToast(
          `이미 만들어진 문서가 있어 ${result.skipped.length}개 묶음은 건너뛰었습니다.`,
          'info',
        );
      }
      setSplitPlan(null);
      setPageState('loading');
      // 앵커로 되돌린다 — 확장 결과의 첫 문서가 placeholder(그룹 1)다.
      setSearchParams({});
      setReloadNonce((n) => n + 1);
    } catch (err) {
      const code = (err as { error?: { code?: string } })?.error?.code;
      if (code === 'REPORT_CREATE_DUPLICATE') {
        // 재확장(다른 기기·중복 탭) — 이미 나뉘어 있으므로 현재 상태를 다시 읽어 작성으로 보낸다.
        showToast('이미 문서가 나뉘어 있어 현재 상태를 다시 불러왔습니다.', 'info');
        setSplitPlan(null);
        setPageState('loading');
        setSearchParams({});
        setReloadNonce((n) => n + 1);
        return;
      }
      if (isTokenInvalidError(err)) {
        setPageState('expired');
        return;
      }
      showToast(apiErrorMessage(err, '문서를 나누지 못했습니다. 다시 시도해주세요.'), 'error');
    } finally {
      setIsExpanding(false);
    }
  };

  if (pageState === 'expired') {
    return (
      <div className="min-h-screen bg-ink-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-card shadow-lg p-8 text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-yellow-100 flex items-center justify-center">
            <span className="text-yellow-600 text-3xl">&#9202;</span>
          </div>
          <h1 className="text-xl font-semibold text-ink-900 mb-2">이 링크는 만료되었습니다</h1>
          <p className="text-ink-600 mb-6">
            유효 기간이 지났거나 이미 제출이 완료된 링크입니다.
          </p>
          <p className="text-sm text-ink-500">담당 안전관리자에게 문의해주세요.</p>
        </div>
      </div>
    );
  }

  if (pageState === 'completed') {
    // 묶음 위임: 잔여 건이 있으면 여기서 끝내지 않고 다음 건으로 이어 준다. 잔여가 없으면
    // 토큰이 서버에서 비활성화되므로(마지막 완료가 끈다) **재조회 없이** 이 화면으로 닫는다.
    // 배포 설정은 위임자(in-app)의 몫이라 외부 화면은 언급조차 하지 않는다(§3 주체 축).
    const isBundle = (draft?.splitSiblings?.length ?? 0) > 1;
    return (
      <div className="min-h-screen bg-ink-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-card shadow-lg p-8 text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-100 flex items-center justify-center">
            <span className="text-green-600 text-3xl">&#10003;</span>
          </div>
          {nextSibling ? (
            <>
              <h1 className="text-xl font-semibold text-ink-900 mb-2">
                이 문서 작성이 완료되었습니다
              </h1>
              <p className="text-ink-600 mb-6">
                남은 문서가 있습니다. 이어서 작성해주세요.
              </p>
              <Button
                type="button"
                variant="primary"
                className="w-full"
                onClick={() => {
                  const target = nextSibling.reportId;
                  setNextSibling(null);
                  setPageState('loading');
                  setSearchParams({ reportId: target });
                }}
              >
                다음 문서 이어 작성 ({nextSibling.label})
              </Button>
            </>
          ) : (
            <>
              <h1 className="text-xl font-semibold text-ink-900 mb-2">
                {isBundle ? '모든 문서 작성이 완료되었습니다' : '작성이 완료되었습니다'}
              </h1>
              <p className="text-ink-600 mb-6">
                {isBundle
                  ? '작성하신 문서는 담당자에게 전달됩니다. 협조해 주셔서 감사합니다.'
                  : '보고서 작성이 완료되었습니다. 협조해 주셔서 감사합니다.'}
              </p>
              <p className="text-sm text-ink-500">
                {isBundle
                  ? '이후 절차는 담당 안전관리자가 진행합니다.'
                  : '담당 안전관리자가 이후 결재를 진행합니다.'}
              </p>
            </>
          )}
        </div>
      </div>
    );
  }

  if (!draft || pageState === 'loading' || pageState === 'error') {
    const isLoading = pageState === 'loading';
    return (
      <ReportWriteShell
        isLoading={isLoading}
        loadingMessage="정보를 불러오는 중..."
        fatalError={isLoading ? undefined : (errorMessage ?? '서버에 연결할 수 없습니다.')}
        maxWidth="max-w-4xl"
      >
        <div />
      </ReportWriteShell>
    );
  }

  // 묶음 위임 그룹 선택 게이트 — **작성 폼보다 앞**(SplitGroupGateScreen doc: 그룹 1의 in-place
  // claim이 확장 전 임시 저장분을 덮으므로, 폼을 먼저 그리면 담당자가 쓴 것이 사라진다).
  if (needsGroupSelection) {
    return (
      <SplitGroupGateScreen
        title={draft.reportTitle}
        subtitle={`${draft.site.name} · 담당자: ${draft.contactName}`}
        message={draft.message}
        isLoading={isPlanLoading}
        error={planError}
        onStart={handleStartGroupSelection}
        // 모달이 아니라 **같은 화면 본문**에 편다(variant="inline") — 게이트+모달 두 겹을 하나로.
        selection={
          splitPlan?.plan ? (
            <SplitGroupSelectModal
              isOpen
              templateName={draft.template.name || draft.reportTitle}
              plan={splitPlan.plan}
              existing={splitPlan.existing}
              isSubmitting={isExpanding}
              onClose={() => setSplitPlan(null)}
              onConfirmSplit={handleConfirmSplit}
              // 통합 = 나누지 않음. 확장을 부르지 않고 placeholder를 그대로 통짜로 쓴다(§6 ②) —
              // 인증 흐름의 combined가 "1건 작성으로 간다"인 것과 같은 의미다.
              onCombined={() => {
                setSplitPlan(null);
                setGroupSelectionSkipped(true);
              }}
              // 무인증 호스트 — 기존 문서 링크는 인증 라우트라 걸지 않는다(모달 prop doc).
              hideExistingLinks
              variant="inline"
            />
          ) : null
        }
      />
    );
  }

  // composeChrome에 preview 항목이 있으면 external에도 미리보기 노출(admin이 빼면 숨김 — 일관성).
  // submit/cancel은 external 흐름(임시저장/완료-서명)에 부적합해 적용하지 않는다.
  const showPreview = (draft.template.composeChrome ?? DEFAULT_COMPOSE_CHROME).items.some(
    (it) => it.actionId === 'preview',
  );

  // 미리보기 뷰 — 전체화면(작성 폼 숨김). 상세와 동일 렌더러(ComposePreviewView, 클라 compose).
  if (mode === 'preview') {
    return (
      <ComposePreviewView
        template={draft.template}
        values={values}
        onBack={() => setMode('edit')}
        restoreScrollY={previewFromScrollYRef.current}
      />
    );
  }

  const daysLeft = daysUntil(draft.expiresAt);

  const rejectionError =
    draft.reportStatus === 'REJECTED'
      ? `반려되었습니다. 내용을 수정한 후 다시 완료해주세요.${draft.rejectionReason ? ` 반려 사유: ${draft.rejectionReason}` : ''}`
      : undefined;

  const infoBannerContent = (() => {
    const baseInfo = '양식을 작성한 후 완료 버튼을 눌러 서명하세요. 변경 내용은 자동으로 저장됩니다.';
    if (draft.message) {
      return (
        <>
          <span>{baseInfo}</span>
          <span className="block mt-1 font-medium">{`관리자 메시지: ${draft.message}`}</span>
        </>
      );
    }
    return baseInfo;
  })();

  const footerLeft = (
    <div className="flex items-center gap-2 text-xs">
      {daysLeft > 0 ? (
        <span className="text-ink-500">{daysLeft}일 남음</span>
      ) : (
        <span className="text-red-500">오늘 마감</span>
      )}
      {isSaving && <span className="text-ink-400">저장 중...</span>}
      {saveError && <span className="text-orange-500">자동 저장 실패</span>}
    </div>
  );

  const footerActions = [
    {
      key: 'save',
      label: isSaving ? '저장 중...' : '임시 저장',
      onClick: handleManualSave,
      variant: 'secondary' as const,
      disabled: isSaving,
      loading: isSaving,
    },
    // 미리보기(composeChrome에 preview 있을 때만) — 임시저장/완료 사이에 배치.
    ...(showPreview
      ? [
          {
            key: 'preview',
            label: '미리보기',
            // 스크롤 위치는 **여기서** 잡아야 한다 — 미리보기 마운트 후엔 문서가 짧아져 잘린 값만 남는다.
            onClick: () => {
              previewFromScrollYRef.current = window.scrollY;
              setMode('preview');
            },
            variant: 'secondary' as const,
          },
        ]
      : []),
    {
      key: 'complete',
      label: '완료',
      onClick: handleCompleteClick,
      variant: 'primary' as const,
    },
  ];

  return (
    <ReportWriteShell
      title={draft.reportTitle}
      subtitle={`${draft.site.name} · 담당자: ${draft.contactName}`}
      footerLeft={footerLeft}
      footerActions={footerActions}
      error={rejectionError}
      infoBanner={infoBannerContent}
      modals={
        <WorkplaceSignModal
          isOpen={showSignModal}
          onClose={() => setShowSignModal(false)}
          defaultSignerName={draft.contactName}
          onSubmit={async ({ signatureData, signerName, signerTitle }) => {
            if (!token) return;
            if (isCompleting) return;
            if (debounceTimer.current) clearTimeout(debounceTimer.current);
            isCompletedRef.current = true;
            setIsCompleting(true);
            try {
              await updateComposedDraft(token, buildSavePayload(values), activeReportId);
              await completeComposedDraft(token, {
                signatureData,
                signerName,
                signerTitle,
                reportId: activeReportId,
              });
              setShowSignModal(false);
              // 묶음이면 다음 이어 작성 후보를 잡는다 — 방금 완료한 이 문서는 제외한다(응답의
              // splitSiblings는 완료 전 스냅샷이라 여기서는 아직 CREATING으로 보인다).
              // 후보 술어가 CREATING뿐인 이유는 nextWritableSibling doc(토큰 수명과 같은 축).
              const next = nextWritableSibling(draft.splitSiblings ?? [], draft.reportId);
              setNextSibling(
                next
                  ? {
                      reportId: next.reportId,
                      label: next.splitGroupLabel || next.splitGroupKey || '다음 문서',
                    }
                  : null,
              );
              setPageState('completed');
            } catch (err) {
              isCompletedRef.current = false;
              // 서버 필수 검증(complete 400)은 클라 게이트와 같은 안내로 — 저장 경합이나 게이트
              // 우회로 여기까지 온 경우다. 모달을 닫아야 안내한 입력으로 돌아갈 수 있다.
              // ⚠️ 이 경로의 VALIDATION_INVALID_INPUT을 만료로 해석하면 안 된다(GET 경로와 달리
              // 여기선 토큰이 아니라 입력이 문제다).
              const serverMissing = (
                err as {
                  error?: {
                    details?: {
                      // 서버 `collectMissingRequiredForComplete` 반환 그대로 — `requirement`는
                      // 2026-09-29 신설이라 배포 창의 구 서버 응답엔 없다(문구가 값 축으로 degrade).
                      missingRequired?: (Pick<MissingRequiredField, 'id' | 'label'> &
                        Partial<Pick<MissingRequiredField, 'requirement'>>)[];
                    };
                  };
                }
              )?.error?.details?.missingRequired;
              if (serverMissing?.length) {
                setShowSignModal(false);
                warnMissingRequired(serverMissing);
              } else {
                showToast(apiErrorMessage(err, '작성 완료에 실패했습니다.'), 'error');
                // 그 외 에러 — 모달 유지, 재시도 허용
              }
            } finally {
              setIsCompleting(false);
            }
          }}
        />
      }
      maxWidth="max-w-4xl"
    >
      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* 묶음 위임 — 카운터 k/N + 형제 이동(형제 2건 이상일 때만 자체 노출, 파생 규약). */}
        {draft.splitSiblings && (
          <SplitSiblingBar
            siblings={draft.splitSiblings}
            currentReportId={draft.reportId}
            onSelect={handleSelectSibling}
            disabled={isSaving || isCompleting}
          />
        )}
        {/* 카탈로그 rows 미해소 장애 안내 — 선택 전용 칸 강등(demoteRequireSelection)과 짝(은닉 금지). */}
        {draft.catalogRowsUnavailable && (
          <div className="mb-4 rounded-card border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            담당자 목록을 불러오지 못해 직접 입력이 허용됩니다. 목록에서 골라야 하는 칸도 이번
            작성에서는 자유롭게 입력할 수 있습니다.
          </div>
        )}
        <div className="bg-white rounded-card border border-ink-200 p-4">
          <h2 className="text-sm font-semibold text-ink-700 mb-3">작성 내용</h2>
          {/* 작성 폼 렌더러는 하나다(2026-09-30 S3-b) — 트리의 출처만 둘(저작/파생, writeTree memo).
              위임 작성(external)은 인증 API(generateImprovementCandidates)를 쓸 수 없어, AI 후보는
              draft 응답의 캐시 히트 한정 동봉분(aiResolvedRows)만 주입한다(2026-07-26 배선 —
              위임 생성 시 위임자 컨텍스트 사전 생성 → inspection 캐시 → 여기서 소비). 미동봉이면
              후보 소스에 의존하는 모달 버튼은 hideWhenNoData로 자동 숨김(구 SMS 동형). */}
          <ComposerWidgetTreeRenderer
            tree={writeTree}
            values={values}
            onChange={handleChange}
            registeredWidgets={COMPOSED_ROW_WIDGETS}
            registeredFormWidgets={COMPOSED_FORM_WIDGETS}
            registeredWidgetContext={{ companyId: draft.company.id, siteId: draft.site.id }}
            dataSources={draft.template.dataSources}
            dataDefs={draft.template.dataDefs}
            resolvedRows={resolvedRows}
            // 공개 경로가 등록할 수 있는 카탈로그를 이 템플릿이 실제로 쓸 때만 배선한다.
            // rc는 콜백 유무 하나로 등록 액션·버튼 노출을 정하므로(키별 구분 없음), 미지원
            // 카탈로그만 쓰는 템플릿에 배선하면 항상 실패하는 버튼이 생긴다.
            onCatalogRowSubmit={hasPublicRegistrableAction ? handleCatalogRowSubmit : undefined}
          />
        </div>
      </div>
    </ReportWriteShell>
  );
}
